/** Mirrors backend ``oral_exam_question_format`` preview helpers for lecturer UI. */

export const FILL_TOKEN = '{{fill}}';
/** Universal preview cue (matches backend ``PREVIEW_CUE``). */
export const PREVIEW_CUE = '…';
/** Older bracketed dummy still present on already-bound questions. */
export const LEGACY_PREVIEW_CUE = '[some case]';

/** True when the question text has the interchangeable-fill slot. */
export function hasFillSlot(questionText: string | null | undefined): boolean {
  return Boolean(questionText?.includes(FILL_TOKEN));
}

export type OralExamFillKind = 'case' | 'kind' | 'scene' | 'problem' | 'locus' | 'specimen';

export const PREVIEW_CUES: Record<OralExamFillKind, string> = {
  case: 'a named example',
  kind: 'a setting of that kind',
  scene: 'a case of that kind',
  problem: 'a problem of that kind',
  locus: 'a phase of that kind',
  specimen: 'an example of that kind',
};

const LEGACY_PREVIEW_CUES: Record<OralExamFillKind, string> = {
  case: '[a company]',
  kind: '[a type of system]',
  scene: '[a situation]',
  problem: '[a problem]',
  locus: '[a phase]',
  specimen: '[some case]',
};

export const FILL_KINDS = Object.keys(PREVIEW_CUES) as OralExamFillKind[];

export const MAX_ORAL_EXAM_FILLS = 5;
export const MAX_FILL_CHARACTERS = 140;
export const MAX_FILL_WORDS = 20;

function tidyHorizontalWhitespaceAround(text: string, anchor: string): string {
  return text
    .split('\n')
    .map((line) => (line.includes(anchor) ? line.replace(/[ \t]+/g, ' ') : line))
    .join('\n');
}

function isFillKind(value: string): value is OralExamFillKind {
  return value in PREVIEW_CUES;
}

/** Drop a leftover ``a`` / ``an`` immediately before the interchangeable slot. */
export function dropLeftoverArticleBeforeSlot(before: string): string {
  return before.replace(/\b(an|a)\s+$/i, '');
}

export type FillAnchorSplit = {
  before: string;
  after: string;
};

export type SplitFillAnchorOptions = {
  /** When true, treat the universal preview cue as a slot for unknown kinds. */
  allowUniversalCue?: boolean;
};

/**
 * Locate the interchangeable-fill slot in preview text so UI can swap it for a
 * kind badge. Lecturer Readonly Preview still contains ``{{fill}}``; student
 * GET already substituted the kind cue. Prefer the token when both exist.
 *
 * Kind and universal cues are preview-only; live bound text must opt in via
 * ``allowUniversalCue`` (and a known ``fillKind`` for kind-specific cues).
 */
export function splitFillAnchor(
  text: string,
  fillKind?: string | null,
  options?: SplitFillAnchorOptions,
): FillAnchorSplit | null {
  if (!text) return null;

  const tokenIndex = text.indexOf(FILL_TOKEN);
  if (tokenIndex >= 0) {
    return {
      before: dropLeftoverArticleBeforeSlot(text.slice(0, tokenIndex)),
      after: text.slice(tokenIndex + FILL_TOKEN.length),
    };
  }

  if (fillKind && isFillKind(fillKind)) {
    for (const kindCue of [PREVIEW_CUES[fillKind], LEGACY_PREVIEW_CUES[fillKind]]) {
      const kindCueIndex = text.indexOf(kindCue);
      if (kindCueIndex >= 0) {
        return {
          before: dropLeftoverArticleBeforeSlot(text.slice(0, kindCueIndex)),
          after: text.slice(kindCueIndex + kindCue.length),
        };
      }
    }
  }

  if (options?.allowUniversalCue) {
    for (const cue of [PREVIEW_CUE, LEGACY_PREVIEW_CUE]) {
      const cueIndex = text.indexOf(cue);
      if (cueIndex >= 0) {
        return {
          before: dropLeftoverArticleBeforeSlot(text.slice(0, cueIndex)),
          after: text.slice(cueIndex + cue.length),
        };
      }
    }
  }

  return null;
}

/** Replace ``{{fill}}`` with the kind-specific preview cue; preserve newlines. */
export function previewQuestion(
  questionText: string,
  _fills?: string[] | null,
  fillKind?: string | null,
): string {
  if (!questionText || !questionText.includes(FILL_TOKEN)) {
    return questionText;
  }
  const cue = (fillKind && isFillKind(fillKind) && PREVIEW_CUES[fillKind]) || PREVIEW_CUE;
  const result = questionText.replaceAll(FILL_TOKEN, cue);
  return tidyHorizontalWhitespaceAround(result, cue);
}

function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

export type FillValidationError = 'tooManyWords' | 'tooManyCharacters';

export function getFillValidationError(value: string): FillValidationError | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.length > MAX_FILL_CHARACTERS) {
    return 'tooManyCharacters';
  }
  if (countWords(trimmed) > MAX_FILL_WORDS) {
    return 'tooManyWords';
  }
  return null;
}

/** Valid, trimmed fills only — for mutate payloads. */
export function sanitizeFillsForSave(fills: string[] | undefined | null): string[] {
  if (!fills?.length) {
    return [];
  }
  return fills
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0 && getFillValidationError(entry) === null)
    .slice(0, MAX_ORAL_EXAM_FILLS);
}
